"use client";

import posthog from "posthog-js";
import {
  isSharedLinkArrival,
  sharedLinkSurface,
} from "@/lib/analytics/share-link";
import { isNativeCapacitorRuntime } from "@/lib/telemetry/runtime";

/**
 * How somebody arrived at an event, resolved once per session and then reused.
 *
 * The queen funnel used to treat the event page as the top of the funnel, so
 * every metric below it divided by a number most people never contributed to:
 * on `one-year-of-tibc`, 142 of 162 people who opened the booking page had
 * never opened the event page at all — the shared link points straight at
 * `/book`. Without an entry attribution the dashboard could only report that
 * booking starts were 257% of views, which reads as a broken metric rather than
 * as a real routing fact.
 *
 * Two properties come out of this, and both are stamped on every funnel event
 * for the event in question:
 *
 * - `entry_point` — where the session came from: another page on Hiive, the
 *   native app, a link off an external site, or a bare direct hit.
 * - `entry_route` — which of this event's two doors they came through first,
 *   the event page or the booking page.
 *
 * They are resolved on first touch and then frozen for the session, because a
 * funnel breakdown is only meaningful if every stage carries the SAME entry
 * attribution. Recomputing per event would attribute stage 4 to "in_app" for
 * everybody — by then the referrer is always Hiive itself.
 */

export type EventEntryPoint =
  | "shared_link"
  | "in_app"
  | "native_app"
  | "external"
  | "direct";

export type EventEntryRoute = "event_page" | "booking_page" | "other";

export type EventEntryAttribution = {
  entry_point: EventEntryPoint;
  entry_route: EventEntryRoute;
  entry_referrer_domain: string | null;
  /**
   * Which surface the share sheet that produced this link was opened from.
   * Null for everything that is not a `shared_link` arrival.
   */
  entry_share_surface: string | null;
  /** True only on the event that resolved the attribution. */
  is_first_touch: boolean;
};

type StoredAttribution = Omit<EventEntryAttribution, "is_first_touch"> & {
  /**
   * The PostHog session this attribution belongs to.
   *
   * `sessionStorage` outlives a PostHog session: a tab left open overnight
   * keeps its storage while PostHog rolls the session after thirty minutes
   * idle. Without this the next morning's visit inherits yesterday's referrer
   * and reports as a continuation of a journey that ended.
   */
  posthog_session_id: string | null;
};

function currentPostHogSessionId(): string | null {
  try {
    return posthog.get_session_id() || null;
  } catch {
    return null;
  }
}

const STORAGE_PREFIX = "hiive:event-entry:";

function storageKey(eventSlug: string) {
  return `${STORAGE_PREFIX}${eventSlug}`;
}

/**
 * `sessionStorage` and not `localStorage`: entry attribution is a fact about
 * this visit. A returning visitor next week arrived some other way, and reusing
 * last week's answer would quietly freeze the acquisition breakdown.
 */
function readStored(eventSlug: string): StoredAttribution | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey(eventSlug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAttribution>;
    if (!parsed.entry_point || !parsed.entry_route) return null;

    // A new PostHog session is a new visit, whatever the tab remembers.
    const sessionId = currentPostHogSessionId();
    if (
      sessionId &&
      parsed.posthog_session_id &&
      parsed.posthog_session_id !== sessionId
    ) {
      return null;
    }

    return {
      entry_point: parsed.entry_point,
      entry_route: parsed.entry_route,
      entry_referrer_domain: parsed.entry_referrer_domain ?? null,
      entry_share_surface: parsed.entry_share_surface ?? null,
      posthog_session_id: parsed.posthog_session_id ?? null,
    };
  } catch {
    return null;
  }
}

function writeStored(eventSlug: string, value: StoredAttribution) {
  try {
    window.sessionStorage.setItem(storageKey(eventSlug), JSON.stringify(value));
  } catch {
    // Private mode and in-app browsers block storage. The attribution is then
    // recomputed per event, which is still correct for the first one and only
    // over-reports `in_app` afterwards — better than throwing inside a capture.
  }
}

function referrerDomain(): string | null {
  try {
    const referrer = document.referrer;
    if (!referrer) return null;
    return new URL(referrer).hostname || null;
  } catch {
    return null;
  }
}

/**
 * Where this visit came from, in priority order.
 *
 * **A share beats everything.** `?ref=share` is put there by our own share
 * sheet, so it is the only signal here that survives an in-app browser — and
 * those strip `document.referrer`, which is exactly why every WhatsApp share
 * used to land in "Direct link". It also outranks `native_app`: somebody
 * opening a shared link inside the app is still a share arrival, and lumping
 * them in with app browsing hides the loop we are trying to measure.
 *
 * **Then an in-app navigation, by breadcrumb rather than by referrer.** The app
 * is a single-page app: a client-side navigation from `/hiives` to an event
 * page never reloads the document, so `document.referrer` still holds whatever
 * loaded the tab an hour ago — usually nothing, which was reported as "direct".
 * `?from=` is written by `BackAwareLink` on in-app links and is a real, first
 * party signal that this route was reached from another one.
 */
function resolveEntryPoint(isNative: boolean): EventEntryPoint {
  const search = window.location.search;
  if (isSharedLinkArrival(search)) return "shared_link";

  if (isInternalNavigationBreadcrumb(search)) return "in_app";

  // The native shell navigates without documents, so `document.referrer` is
  // empty for reasons that have nothing to do with acquisition.
  if (isNative) return "native_app";

  const domain = referrerDomain();
  if (!domain) return "direct";
  if (domain === window.location.hostname) return "in_app";
  return "external";
}

/**
 * A `?from=` that names an internal path.
 *
 * Checked rather than merely present: the value goes nowhere near a navigation
 * here, but treating `?from=https://evil.example` as proof of in-app browsing
 * would let anybody relabel their own traffic in a queen's dashboard.
 */
function isInternalNavigationBreadcrumb(search: string): boolean {
  try {
    const from = new URLSearchParams(search).get("from");
    return Boolean(from?.startsWith("/") && !from.startsWith("//"));
  } catch {
    return false;
  }
}

/**
 * Resolve (and freeze) how this session reached `eventSlug`.
 *
 * `route` is what the caller knows about itself — the event page passes
 * `event_page`, the booking page passes `booking_page`. Whichever fires first
 * in the session is the one that gets recorded.
 */
export function resolveEventEntry(input: {
  eventSlug: string;
  route: EventEntryRoute;
}): EventEntryAttribution {
  if (typeof window === "undefined") {
    return {
      entry_point: "direct",
      entry_route: input.route,
      entry_referrer_domain: null,
      entry_share_surface: null,
      is_first_touch: true,
    };
  }

  const stored = readStored(input.eventSlug);
  if (stored) {
    const { posthog_session_id: _sessionId, ...attribution } = stored;
    return { ...attribution, is_first_touch: false };
  }

  const resolved: StoredAttribution = {
    entry_point: resolveEntryPoint(isNativeCapacitorRuntime()),
    entry_route: input.route,
    entry_referrer_domain: referrerDomain(),
    // The domain the share was opened FROM — WhatsApp, Instagram, a Google
    // result — is the other half of the story, and it is the half a queen can
    // act on. Kept even when the entry point is `shared_link`.
    entry_share_surface: sharedLinkSurface(window.location.search),
    posthog_session_id: currentPostHogSessionId(),
  };
  writeStored(input.eventSlug, resolved);
  const { posthog_session_id: _new, ...attribution } = resolved;
  return { ...attribution, is_first_touch: true };
}
